import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  FileText, Send, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Eye, ArrowDownCircle, ArrowUpCircle, MessageSquare, User,
  Calendar, Download, Plus,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BoardRaporlar = () => {
  const { branchId } = useParams();
  const { branches } = useOutletContext();
  const [mainTab, setMainTab] = useState('incoming');
  
  // Reports state
  const [incomingReports, setIncomingReports] = useState([]);
  const [outgoingReports, setOutgoingReports] = useState([]);
  const [reportRequests, setReportRequests] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(false);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const branch = branches?.find(b => b.id === branchId);
  const branchName = branch?.name || 'Şube';

  useEffect(() => {
    loadAll();
  }, [branchId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [reportsRes, requestsRes] = await Promise.all([
        axios.get(`${API_URL}/api/board/branch/${branchId}/reports`, { headers }),
        axios.get(`${API_URL}/api/board/branch/${branchId}/report-requests`, { headers })
      ]);
      setIncomingReports(reportsRes.data?.incoming || []);
      setOutgoingReports(reportsRes.data?.outgoing || []);
      setReportRequests(requestsRes.data || { sent: [], received: [] });
    } catch (error) {
      console.error('Raporlar yüklenirken hata:', error);
      toast.error('Raporlar yüklenemedi');
    }
    setLoading(false);
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700', icon: FileText },
      sent: { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700', icon: Send },
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700', icon: Clock },
      read: { label: 'Okundu', color: 'bg-indigo-100 text-indigo-700', icon: Eye },
      accepted: { label: 'Kabul Edildi', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      approved: { label: 'Onaylandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: XCircle },
      completed: { label: 'Tamamlandı', color: 'bg-green-100 text-green-700', icon: CheckCircle }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isPreviewable = (type) => {
    return type?.startsWith('image/') || type === 'application/pdf';
  };

  const downloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = doc.data;
    link.download = doc.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Raporlar için sıralama
  const sortItems = (items, type) => {
    if (!sortConfig.key) return items;
    
    return [...items].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'title':
          aValue = (a.title || '').toLowerCase();
          bValue = (b.title || '').toLowerCase();
          break;
        case 'sender':
          if (type === 'incoming' || type === 'received') {
            aValue = (a.from_user?.name || a.from_user_name || '').toLowerCase();
            bValue = (b.from_user?.name || b.from_user_name || '').toLowerCase();
          } else {
            aValue = (a.to_user?.name || '').toLowerCase();
            bValue = (b.to_user?.name || '').toLowerCase();
          }
          break;
        case 'status':
          const statusOrder = { approved: 5, accepted: 4, read: 3, sent: 2, pending: 1, draft: 0, rejected: -1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Tümünü seç
  const handleSelectAll = (items) => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(item => item.id));
    }
  };

  // Tek seç
  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Rapor istekleri birleştir (gönderilen + alınan)
  const allRequests = [...(reportRequests.sent || []), ...(reportRequests.received || [])];

  const renderReportTable = (reports, type) => {
    if (reports.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
          <h3 className="text-lg font-medium mb-2">Rapor Bulunmuyor</h3>
          <p className="text-sm">Bu kategoride rapor yok</p>
        </div>
      );
    }

    const sortedReports = sortItems(reports, type);

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox 
                checked={selectedIds.length === reports.length && reports.length > 0}
                onCheckedChange={() => handleSelectAll(reports)}
              />
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('title')}
            >
              <div className="flex items-center gap-1">
                Rapor Başlığı
                <SortIcon columnKey="title" />
              </div>
            </TableHead>
            <TableHead>Rapor İçeriği</TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('sender')}
            >
              <div className="flex items-center gap-1">
                {type === 'incoming' ? 'Gönderen' : 'Alıcı'}
                <SortIcon columnKey="sender" />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                Durum
                <SortIcon columnKey="status" />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center gap-1">
                Tarih
                <SortIcon columnKey="created_at" />
              </div>
            </TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedReports.map(report => (
            <TableRow key={report.id} data-testid={`report-row-${report.id}`}>
              <TableCell>
                <Checkbox 
                  checked={selectedIds.includes(report.id)}
                  onCheckedChange={() => handleSelectOne(report.id)}
                />
              </TableCell>
              <TableCell>
                <p className="font-medium">{report.title}</p>
              </TableCell>
              <TableCell>
                {report.content ? (
                  <p className="text-sm text-gray-500 truncate max-w-[200px]">{report.content}</p>
                ) : (
                  <span className="text-gray-400 text-sm">-</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  {type === 'incoming' 
                    ? (report.from_user?.name || report.from_user_name || 'Bilinmiyor')
                    : (report.to_user?.name || 'Bilinmiyor')}
                </div>
              </TableCell>
              <TableCell>{getStatusBadge(report.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  {report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR') : '-'}
                  {report.created_at && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(report.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { 
                    setSelectedItem(report); 
                    setSelectedType(type);
                    setDetailModalOpen(true); 
                  }}
                  className="gap-1"
                >
                  <Eye className="h-4 w-4" />
                  Detay
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const renderRequestTable = (requests) => {
    if (requests.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-200" />
          <h3 className="text-lg font-medium mb-2">Rapor İsteği Bulunmuyor</h3>
          <p className="text-sm">Bu kategoride rapor isteği yok</p>
        </div>
      );
    }

    const sortedRequests = sortItems(requests, 'requests');

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <Checkbox 
                checked={selectedIds.length === requests.length && requests.length > 0}
                onCheckedChange={() => handleSelectAll(requests)}
              />
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('title')}
            >
              <div className="flex items-center gap-1">
                Rapor İsteği
                <SortIcon columnKey="title" />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('sender')}
            >
              <div className="flex items-center gap-1">
                İsteyen / Hedef
                <SortIcon columnKey="sender" />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center gap-1">
                Durum
                <SortIcon columnKey="status" />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-50 select-none"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center gap-1">
                Tarih
                <SortIcon columnKey="created_at" />
              </div>
            </TableHead>
            <TableHead className="text-right">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedRequests.map(req => (
            <TableRow key={req.id} data-testid={`request-row-${req.id}`}>
              <TableCell>
                <Checkbox 
                  checked={selectedIds.includes(req.id)}
                  onCheckedChange={() => handleSelectOne(req.id)}
                />
              </TableCell>
              <TableCell>
                <div>
                  <p className="font-medium">{req.title}</p>
                  {req.description && (
                    <p className="text-sm text-gray-500 truncate max-w-[300px]">{req.description}</p>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  {req.from_user?.name || req.to_user?.name || 'Bilinmiyor'}
                </div>
              </TableCell>
              <TableCell>{getStatusBadge(req.status)}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-4 w-4" />
                  {req.created_at ? new Date(req.created_at).toLocaleDateString('tr-TR') : '-'}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => { 
                    setSelectedItem(req); 
                    setSelectedType('request');
                    setDetailModalOpen(true); 
                  }}
                  className="gap-1"
                >
                  <Eye className="h-4 w-4" />
                  Detay
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-6" data-testid="board-raporlar">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase">RAPORLAR</h1>
          <p className="text-gray-500">{branchName} raporlarını yönetin</p>
        </div>
        <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Yenile
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <>
          {/* Özet Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <ArrowDownCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{incomingReports.length}</p>
                    <p className="text-sm text-gray-500">Gelen Rapor</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-100 rounded-full">
                    <ArrowUpCircle className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{outgoingReports.length}</p>
                    <p className="text-sm text-gray-500">Giden Rapor</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <MessageSquare className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-gray-900">{allRequests.length}</p>
                    <p className="text-sm text-gray-500">Rapor İsteği</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sekmeler ve Tablo */}
          <Card>
            <CardContent className="p-0">
              <Tabs value={mainTab} onValueChange={(val) => { setMainTab(val); setSelectedIds([]); }}>
                <div className="border-b px-4 pt-4">
                  <TabsList className="bg-transparent gap-4">
                    <TabsTrigger 
                      value="incoming" 
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:shadow-none rounded-none pb-3 gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      GELEN RAPORLAR
                      {incomingReports.length > 0 && (
                        <Badge className="ml-1 bg-blue-100 text-blue-700">{incomingReports.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="outgoing" 
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:shadow-none rounded-none pb-3 gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      GİDEN RAPORLAR
                      {outgoingReports.length > 0 && (
                        <Badge className="ml-1 bg-amber-100 text-amber-700">{outgoingReports.length}</Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="requests" 
                      className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-amber-500 data-[state=active]:shadow-none rounded-none pb-3 gap-2"
                    >
                      <MessageSquare className="h-4 w-4" />
                      RAPOR İSTEKLERİ
                      {allRequests.length > 0 && (
                        <Badge className="ml-1 bg-purple-100 text-purple-700">{allRequests.length}</Badge>
                      )}
                    </TabsTrigger>
                  </TabsList>
                </div>

                <div className="p-4">
                  {/* Tümünü seç ve seçim bilgisi */}
                  {((mainTab === 'incoming' && incomingReports.length > 0) ||
                    (mainTab === 'outgoing' && outgoingReports.length > 0) ||
                    (mainTab === 'requests' && allRequests.length > 0)) && (
                    <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          id="select-all"
                          checked={selectedIds.length === (
                            mainTab === 'incoming' ? incomingReports.length :
                            mainTab === 'outgoing' ? outgoingReports.length :
                            allRequests.length
                          )}
                          onCheckedChange={() => handleSelectAll(
                            mainTab === 'incoming' ? incomingReports :
                            mainTab === 'outgoing' ? outgoingReports :
                            allRequests
                          )}
                        />
                        <label htmlFor="select-all" className="cursor-pointer">Tümünü seç</label>
                      </div>
                      {selectedIds.length > 0 && (
                        <span className="text-amber-600">{selectedIds.length} öğe seçildi</span>
                      )}
                    </div>
                  )}

                  <TabsContent value="incoming" className="mt-0">
                    {renderReportTable(incomingReports, 'incoming')}
                  </TabsContent>

                  <TabsContent value="outgoing" className="mt-0">
                    {renderReportTable(outgoingReports, 'outgoing')}
                  </TabsContent>

                  <TabsContent value="requests" className="mt-0">
                    {renderRequestTable(allRequests)}
                  </TabsContent>
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}

      {/* Detay Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              {selectedType === 'request' ? 'Rapor İsteği Detayı' : 'Rapor Detayı'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  {getStatusBadge(selectedItem.status)}
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedItem.title}</h3>
                {selectedItem.content && (
                  <p className="text-gray-600">{selectedItem.content}</p>
                )}
                {selectedItem.description && (
                  <p className="text-gray-600">{selectedItem.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {selectedType === 'incoming' && (
                  <div>
                    <Label className="text-gray-500">Gönderen</Label>
                    <p className="font-medium">{selectedItem.from_user?.name || selectedItem.from_user_name || 'Bilinmiyor'}</p>
                  </div>
                )}
                {selectedType === 'outgoing' && (
                  <div>
                    <Label className="text-gray-500">Alıcı</Label>
                    <p className="font-medium">{selectedItem.to_user?.name || 'Bilinmiyor'}</p>
                  </div>
                )}
                {selectedType === 'request' && (
                  <>
                    <div>
                      <Label className="text-gray-500">İsteyen</Label>
                      <p className="font-medium">{selectedItem.from_user?.name || 'Bilinmiyor'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Hedef</Label>
                      <p className="font-medium">{selectedItem.to_user?.name || 'Bilinmiyor'}</p>
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">
                    {selectedItem.created_at 
                      ? new Date(selectedItem.created_at).toLocaleDateString('tr-TR', { 
                          day: '2-digit', month: '2-digit', year: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })
                      : '-'}
                  </p>
                </div>
                {selectedItem.due_date && (
                  <div>
                    <Label className="text-gray-500">Son Tarih</Label>
                    <p className="font-medium">{new Date(selectedItem.due_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {/* Belgeler */}
              {selectedItem.documents && selectedItem.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block">Ekli Belgeler</Label>
                  <div className="space-y-2">
                    {selectedItem.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-500" />
                          <div>
                            <p className="font-medium text-sm">{doc.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {isPreviewable(doc.type) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadDocument(doc)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailModalOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Önizleme Modal */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-500" />
              {previewDoc?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-4 bg-gray-100 rounded-lg min-h-[400px] max-h-[70vh] overflow-auto">
            {previewDoc?.type?.startsWith('image/') ? (
              <img 
                src={previewDoc?.data} 
                alt={previewDoc?.name}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
              />
            ) : previewDoc?.type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.data}
                title={previewDoc?.name}
                className="w-full h-[65vh] border-0 rounded"
              />
            ) : (
              <p className="text-gray-500">Bu dosya türü önizlenemiyor</p>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => downloadDocument(previewDoc)}>
              <Download className="h-4 w-4 mr-2" />
              İndir
            </Button>
            <Button onClick={() => setPreviewDoc(null)}>Kapat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BoardRaporlar;
